import { Suspense, useEffect } from 'react'
import { Navigate, NavLink, Outlet, useLocation, useParams } from 'react-router-dom'

import { t } from '../i18n'
import { Pending } from '../ui'
import { useScreen } from './screen'

/**
 * Directory and permissions, as one place with five sections in it.
 *
 * The four screens were four entries in the job's menu — the project
 * directory, People, Access and Access requests — and two of them left the
 * shell for a page with no job around it. Somebody fixing why one person cannot
 * open an RFI moves between all four, and each hop was a trip through the menu
 * or out of the job and back. Here they sit side by side under one entry, and
 * the job stays open around them.
 *
 * The fifth is the project's notification e-mail: its default settings, its
 * distribution lists for general notices and its e-mail templates. It sits
 * here because the same administrator sets it, and the server refuses anybody
 * else.
 *
 * **What each section is about is said on the section, not left to the
 * address.** The project directory is this job's: who is on it and for whom.
 * People and the request queue are the whole site's, and Access is held per
 * job across all of them. Two of the four have a list headed "People", so the
 * scope beside each name is what tells somebody which of the two they are
 * changing.
 *
 * **Nothing here decides who may use a section.** The entry that leads here is
 * locked on the directory's level, the same level each of the four entries was
 * locked on before; every section is the same screen it was, and the server
 * refuses what it refused. Integrations stays on the administration rail for
 * the Global Admin alone, and the addresses each screen had before still open
 * it.
 */

export type DirectorySection = {
  key: 'directory' | 'people' | 'access' | 'requests' | 'mail'
  /** Under `/p/<project>/directory`, with no leading slash; empty is the area's own page. */
  slug: string
  label: string
  scope: string
}

export function directorySections(): DirectorySection[] {
  return [
    { key: 'directory', slug: '', label: t('dir.title'), scope: t('area.scope.job') },
    { key: 'people', slug: 'people', label: t('people.admin.title'), scope: t('area.scope.site') },
    { key: 'access', slug: 'access', label: t('access.title'), scope: t('area.scope.every_job') },
    { key: 'requests', slug: 'access-requests', label: t('requests.title'), scope: t('area.scope.site') },
    { key: 'mail', slug: 'notification-mail', label: t('mail.area.label'), scope: t('area.scope.job') },
  ]
}

export function directorySectionPath(projectId: string, section: DirectorySection): string {
  return section.slug ? `/p/${projectId}/directory/${section.slug}` : `/p/${projectId}/directory`
}

/**
 * The help each section opens.
 *
 * The directory and People declare their own, the way every screen in the
 * shell does. Access and the request queue were written for a page outside the
 * shell, where there was no help drawer to tell, so the area tells it for them;
 * without this their `?` would open whichever screen's help was open before.
 */
const HELP: Partial<Record<DirectorySection['key'], string>> = {
  access: '/directory/effective-permissions',
  requests: '/directory/access-requests',
}

export function DirectoryArea() {
  const { projectId = '' } = useParams<{ projectId: string }>()
  const { pathname } = useLocation()
  const { setHelpRoute, setModuleCode } = useScreen()
  const sections = directorySections()
  // Matched on whole path segments, the way the shell matches its entries:
  // `access` is a prefix of `access-requests`, so a bare `startsWith` would
  // read the request queue as Access and hand it Access's help and report
  // route.
  const current =
    sections.filter((one) => one.slug).find((one) => {
      const path = directorySectionPath(projectId, one)
      return pathname === path || pathname.startsWith(`${path}/`)
    }) ?? sections[0]
  const help = current ? HELP[current.key] : undefined

  useEffect(() => {
    if (!help) return
    setHelpRoute(help)
    setModuleCode('CM-TEN')
  }, [help, setHelpRoute, setModuleCode])

  return (
    <div className="directory-area">
      <nav className="area-sections" aria-label={t('area.title')}>
        <p className="area-title">{t('area.title')}</p>
        <ul>
          {sections.map((one) => (
            <li key={one.key}>
              {/* The scope describes the link rather than naming it, so a
                  section is still called what its own heading calls it. */}
              <NavLink
                to={directorySectionPath(projectId, one)}
                end={!one.slug}
                aria-describedby={`area-scope-${one.key}`}
              >
                <span className="area-label">{one.label}</span>
                <span className="area-scope" id={`area-scope-${one.key}`} aria-hidden="true">
                  {one.scope}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      {/* Its own fallback, so a section loading for the first time waits
          under the sections rather than taking them off the screen. */}
      <Suspense fallback={<Pending />}>
        <Outlet />
      </Suspense>
    </div>
  )
}

/**
 * An address from before the area, sent on to the same screen inside it.
 *
 * People was at `/p/<project>/people`, and that address is in bookmarks and in
 * messages people have sent each other. It is replaced rather than pushed, so
 * Back does not land on an address that only ever moves on again, and whatever
 * followed the path comes along with it.
 */
export function MovedIntoDirectory({ slug }: { slug: string }) {
  const { projectId = '' } = useParams<{ projectId: string }>()
  const { search, hash } = useLocation()
  return <Navigate to={`/p/${projectId}/directory/${slug}${search}${hash}`} replace />
}
